import React from 'react';

const SITE_URL = 'https://podbrowser.ai';

const post = {
  slug: 'podbrowser',
  title: 'PodBrowser: a morning paper for the podcasts you never have time for',
  date: '2026-08-01',
  cover: '/blog/podbrowser/episode.jpg',
  coverAlt:
    'A PodBrowser episode page: the a16z interview with Amjad Masad on rethinking college, set as a newspaper article with a sidebar for Briefing, Ask, Topics, Shows and Saved',
  coverCaption: 'Every episode, written up as an article.',
  excerpt:
    'PodBrowser listens to the crypto, markets and tech podcasts we follow, transcribes every episode and turns each day into a short briefing. Here is what it does and how it works.',
  content: (
    <>
      <p>
        There are more good podcasts than there are hours to listen to them.
        In crypto, markets and tech it is worse, because the shows that matter
        publish daily and the one useful idea in a ninety-minute conversation
        might arrive at minute seventy.{' '}
        <a href={SITE_URL}>PodBrowser</a> is our answer to that. It listens to
        every episode so you do not have to, and every day it hands you a
        briefing you can read over coffee. When an episode is worth your ears,
        you will know.
      </p>

      <figure className="post-figure">
        <img
          src="/blog/podbrowser/briefing.jpg"
          alt="The PodBrowser morning briefing for 23 September 2026: themes such as Bitcoin price and crypto regulation, each with a headline and points tagged with the show they came from, beside an Ask the archive panel"
        />
        <figcaption>The morning briefing. Every point is tagged with the show it came from.</figcaption>
      </figure>

      <h2>What you get</h2>
      <p>
        PodBrowser follows a hand-picked set of shows: crypto and markets
        mainstays like Bankless, Unchained, Empire and The Breakdown, macro and
        investing shows like Odd Lots, and tech and AI conversations from the
        likes of a16z and All-In.
      </p>
      <p>
        <strong>The daily briefing</strong> is the front page. Each day&apos;s
        episodes are grouped into a handful of themes, each with a headline
        and a few sharp points, and every point links to the episodes it came
        from. If three shows spent Tuesday arguing about the same bill, you
        get one theme with all three voices in it, not three separate
        summaries saying the same thing.
      </p>
      <p>
        <strong>Every episode</strong> also gets its own page: a short preview
        that tells you in one paragraph what it is actually about, and a full
        summary written up as an article, covering the claims, the numbers,
        who disagreed with whom, and what anyone predicted.
      </p>
      <p>
        <strong>Topics</strong> follow a subject across days. Open one and you
        see this week&apos;s points on it, how many episodes touched it each
        day, and which shows keep coming back to it.
      </p>
      <p>
        <strong>Ask the archive</strong> is for everything else. Type a
        question, such as what people have been saying about stablecoin
        regulation this month, and PodBrowser answers it from the most
        relevant summaries, with numbered sources so you can check every line
        against the episode it came from.
      </p>

      <figure className="post-figure">
        <img
          src="/blog/podbrowser/ask.jpg"
          alt="Ask the archive answering 'Who is bearish on Bitcoin right now?' with three cited points and source cards naming the show, date and episode for each"
        />
        <figcaption>Ask the archive. Every point cites the episode it came from.</figcaption>
      </figure>

      <h2>Keeping the summaries honest</h2>
      <p>
        The hard part of summarising podcasts is not making them shorter. It
        is keeping them honest. A long conversation wanders, hosts read ads,
        guests repeat themselves, and a careless summary will happily turn a
        guess into a fact or fill a gap with something the model half
        remembers.
      </p>
      <p>
        So PodBrowser does not summarise in one pass. It reads the transcript
        in chunks and first pulls out evidence: each claim, number, decision,
        disagreement, prediction and caveat, with who said it. Only then does
        it write, working from those notes up to a draft and from the draft to
        the final summary. At every step it is told to use only what is in the
        episode, to keep names, figures and dates exact, and to keep the
        nuance: the uncertainty, the trade-offs, the conditions attached to a
        bold call. Sponsor reads, housekeeping and the generic wrap-up are
        stripped out.
      </p>
      <p>
        The result reads less like a blurb and more like notes from a
        colleague who sat through the whole thing and cares about getting it
        right. It is still a machine summary, so treat it as a guide to what
        is worth hearing, not a replacement for the episode. That is why
        everything in PodBrowser, from a briefing point to an Ask answer,
        links back to its source.
      </p>

      <h2>How it works</h2>
      <p>
        Under the hood it is a pipeline. Every three hours it checks each
        show&apos;s RSS feed and downloads anything new. It transcribes the
        audio with Whisper, splitting long episodes into chunks so a
        three-hour interview never has to sit in memory all at once. It
        writes the preview and the full summary. Then it looks across the
        day&apos;s summaries and groups them into the briefing&apos;s themes,
        rebuilding a day only when a new episode has landed in it. The web
        app on top is Next.js, and the whole thing runs on Google Cloud: Cloud
        Run for the site and the scheduled jobs, and a MySQL database holding
        every episode, transcript, summary and briefing.
      </p>
      <p>
        It did not start that way. For most of its life the pipeline ran as a
        set of background jobs on a Mac, with a watchdog script to restart it
        when it fell over. Now it has its own cloud project, and it deploys
        itself whenever we push a change.
      </p>

      <h2>What running it has taught us</h2>
      <p>
        A pipeline that runs every three hours finds every weakness you have.
        A few lessons we paid for:
      </p>
      <ul>
        <li>
          <strong>One bad episode can stop everything.</strong> The queue works
          oldest first, so a single broken audio file or an unusually long
          episode can block everything behind it. When the summaries stop,
          look at the oldest thing still waiting.
        </li>
        <li>
          <strong>Every network call needs a timeout.</strong> One podcast
          host that never answers should not be able to hang the whole run.
        </li>
        <li>
          <strong>Pin your dependencies.</strong> Unpinned packages have broken
          production twice, once when an AI SDK quietly dropped a parameter we
          relied on. Everything is pinned now and upgraded one at a time.
        </li>
        <li>
          <strong>Audio is bigger than you think.</strong> Long episodes and
          scratch files on an in-memory disk add up fast. Chunking the
          transcription fixed a run of out-of-memory crashes.
        </li>
      </ul>

      <h2>A new edition</h2>
      <p>
        The briefing, topics and Ask all arrive with a redesign. PodBrowser
        used to look like a feed reader. Now it reads like a newspaper: serif
        headlines on warm paper, a sidebar for finding your way around, and
        pages that load fast because they read straight from the database.
        Along the way we cut the parts that no longer earned their place, so
        what is left is the part that matters: what was said, by whom, and where to hear it.
      </p>
      <p>
        <a href={SITE_URL}>Have a look at podbrowser.ai</a>. Read
        today&apos;s briefing, ask it about something you have been meaning
        to catch up on, and see what you missed.
      </p>
    </>
  ),
};

export default post;
